import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Reveal, Eyebrow } from "./Reveal";
import { productPath } from "@/lib/seo";
import { displayTitle } from "@/lib/title";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

const imgFallback = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = "/assets/favicon.png";
  e.currentTarget.classList.remove("object-cover", "group-hover:scale-105");
  e.currentTarget.classList.add("object-contain", "p-10", "bg-[#F5F3EC]");
};

const CATEGORIES = [
  {
    id: "watches",
    name: "Watches",
    note: "Luxury & mid-range timepieces",
    to: "/collections/watches",
    img: "/assets/showcase/watches.jpg",
  },
  {
    id: "chains",
    name: "Gold Chains",
    note: "10K, 14K & 18K solid",
    to: "/catalog?category=Chains",
    img: "/assets/showcase/gold-chains.jpg",
  },
  {
    id: "rings",
    name: "Diamond & Gold Rings",
    note: "Solitaires, bands & halos",
    to: "/catalog?category=Rings",
    img: "/assets/showcase/rings.jpg",
  },
  {
    id: "earrings",
    name: "Earrings & Studs",
    note: "Hoops, drops & clusters",
    to: "/catalog?category=Earrings",
    img: "/assets/showcase/earrings.jpg",
  },
  {
    id: "pendants",
    name: "Pendants & Medallions",
    note: "Diamond crosses & charms",
    to: "/catalog?category=Pendants+%26+Charms",
    img: "/assets/showcase/pendants.jpg",
  },
  {
    id: "nameplates",
    name: "Custom Nameplates",
    note: "Engravable plates & hoops",
    to: "/collections/nameplates",
    img: "/assets/showcase/nameplates.jpg",
  },
];

export const ProductMarquee = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/products/featured?limit=24`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  const loop = [...items, ...items];

  return (
    <section data-testid="product-marquee-section" className="py-20 lg:py-24 bg-[#FDFBF7] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow>Explore our signature collections</Eyebrow>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-neutral-900 mt-4">
            Straight from the <span className="text-[#C9A227]">showcase</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5" data-testid="category-strip">
            {CATEGORIES.map((c) => {
              const cardClass =
                "group bg-white border border-[#E8DCC4] rounded-sm overflow-hidden hover:border-[#C9A227] hover:shadow-[0_16px_36px_rgba(201,162,39,0.18)] transition-[border-color,box-shadow] duration-300";
              const cardBody = (
                <>
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={c.img}
                      alt={c.name}
                      loading="lazy"
                      onError={imgFallback}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent" />
                  </div>
                  <div className="px-4 py-4">
                    <p className="font-display text-sm sm:text-base text-neutral-900 leading-snug">{c.name}</p>
                    <p className="text-xs text-neutral-500 mt-1">{c.note}</p>
                  </div>
                </>
              );
              return c.external ? (
                <a
                  key={c.id}
                  href={c.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`category-card-${c.id}`}
                  className={cardClass}
                >
                  {cardBody}
                </a>
              ) : (
                <Link key={c.id} to={c.to} data-testid={`category-card-${c.id}`} className={cardClass}>
                  {cardBody}
                </Link>
              );
            })}
          </div>
        </Reveal>
      </div>

      {loop.length > 0 && (
        <div
          className="marquee-track mt-14 [mask-image:linear-gradient(to_right,transparent_0%,black_5%,black_95%,transparent_100%)]"
          data-testid="product-marquee"
        >
          <div className="animate-marquee-slow flex gap-4 sm:gap-6 w-max">
            {loop.map((p, i) => (
              <Link
                key={`${p.sku}-${i}`}
                to={productPath(p)}
                data-testid={i < items.length ? `marquee-card-${p.sku}` : undefined}
                className="group w-[220px] sm:w-[280px] shrink-0 bg-white border border-[#E8DCC4] rounded-sm shadow-[0_8px_24px_rgba(201,162,39,0.06)] hover:border-[#C9A227] hover:shadow-[0_16px_36px_rgba(201,162,39,0.18)] transition-[border-color,box-shadow] duration-300"
              >
                <div className="h-[180px] sm:h-[210px] overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={displayTitle(p)}
                    loading="lazy"
                    onError={imgFallback}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                  />
                </div>
                <div className="px-4 py-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#C9A227] font-semibold">{p.category}</p>
                  <p className="text-sm font-semibold text-neutral-900 leading-snug mt-1 truncate">
                    {displayTitle(p)}
                  </p>
                  <p className="mt-2 flex items-center gap-2">
                    <span className="font-bold text-neutral-900">{money(p.retail_price)}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                      In stock
                    </span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
